import { createContext, useCallback, useContext, useState, type ReactNode } from 'react'

interface ChatBotContextValue {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  open: () => void
  close: () => void
  toggle: () => void
  /**
   * True while a full-screen overlay (e.g. the mobile drawer) is open.
   * The launcher hides itself and any open panel collapses, so the chatbot
   * never bleeds through the menu.
   */
  isHiddenByOverlay: boolean
  setOverlayOpen: (open: boolean) => void
}

const ChatBotContext = createContext<ChatBotContextValue | null>(null)

export function ChatBotProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [isHiddenByOverlay, setIsHiddenByOverlay] = useState(false)

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])
  const toggle = useCallback(() => setIsOpen((v) => !v), [])
  const setOverlayOpen = useCallback((next: boolean) => {
    setIsHiddenByOverlay(next)
    if (next) setIsOpen(false)
  }, [])

  return (
    <ChatBotContext.Provider
      value={{
        isOpen,
        setIsOpen,
        open,
        close,
        toggle,
        isHiddenByOverlay,
        setOverlayOpen,
      }}
    >
      {children}
    </ChatBotContext.Provider>
  )
}

export function useChatBot() {
  const context = useContext(ChatBotContext)
  if (!context) {
    throw new Error('useChatBot must be used within a ChatBotProvider')
  }
  return context
}
